/**
 * Centralized toast wrapper around `react-hot-toast`.
 *
 * Why this exists:
 *  - One place to extract messages from `ApiError` / `Error` / `unknown`.
 *  - Auto-dedupes identical errors fired in a short window (mutation
 *    cascades, polling errors) so the user sees one toast, not five.
 *  - Translates HTTP semantics into friendly copy (`status === 0` →
 *    "You appear to be offline"; `401` is silent because the
 *    `SessionExpiredBanner` owns that flow).
 *  - Lets us add Sentry / analytics hooks in one place later.
 *
 * Convention: prefer `notify.error(err, "Couldn't <verb>")` over passing
 * a hand-rolled string. The wrapper falls back to the verb form only when
 * the underlying error has no useful message, so happy-path detail still
 * surfaces ("Title must be at most 120 characters").
 *
 * IMPORTANT: do not import `react-hot-toast` anywhere else. An ESLint
 * rule (added in Phase 4) enforces this - the `<Toaster>` mount in
 * `App.tsx` is the only sanctioned exception.
 */

import { createElement } from 'react';
// eslint-disable-next-line no-restricted-imports
import toast from 'react-hot-toast';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Structural duck-type for `ApiError` from `api-client.ts`. Avoids a
 *  circular import - we only read three fields. */
interface ApiErrorLike {
  name: 'ApiError';
  status: number;
  detail: string;
  message?: string;
}

function isApiError(err: unknown): err is ApiErrorLike {
  return (
    typeof err === 'object' &&
    err !== null &&
    (err as { name?: unknown }).name === 'ApiError' &&
    typeof (err as { status?: unknown }).status === 'number'
  );
}

// ---------------------------------------------------------------------------
// Message extraction
// ---------------------------------------------------------------------------

/** Substrings that look like an error message but tell the user nothing.
 *  When the extracted message matches one of these, the caller's
 *  `fallback` wins instead. */
const GENERIC_MESSAGES = new Set([
  '',
  'error',
  'Error',
  'unknown error',
  'Unknown error',
  'Internal Server Error',
  'Bad Request',
  'Failed',
  'Request failed',
  'Network error',
  'TypeError: Failed to fetch',
]);

function rawMessage(err: unknown): string {
  if (isApiError(err)) return err.detail || err.message || '';
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  return '';
}

/** "Failed to save" → "Couldn't save". Cheap textual normalization that
 *  fixes the most common tense inconsistency the recon found across 27
 *  files without trying to auto-rewrite arbitrary copy. */
function normalizeTense(msg: string): string {
  if (msg.startsWith('Failed to ')) return `Couldn't ${msg.slice(10)}`;
  return msg;
}

function resolveErrorMessage(err: unknown, fallback?: string): string {
  // HTTP-status-aware overrides take precedence over any text from the
  // backend - these are user-facing, not developer-facing.
  if (isApiError(err)) {
    if (err.status === 0) return 'You appear to be offline.';
    if (err.status === 403) return fallback || "You don't have access to this.";
    if (err.status === 404 && fallback) return fallback;
    if (err.status >= 500) {
      return fallback || 'Something went wrong on our end.';
    }
  }

  const raw = rawMessage(err).trim();
  if (raw && !GENERIC_MESSAGES.has(raw)) return normalizeTense(raw);
  return fallback || 'Something went wrong.';
}

// ---------------------------------------------------------------------------
// Dedupe key
// ---------------------------------------------------------------------------

/** Stable id used by `react-hot-toast` to merge consecutive identical
 *  toasts. `toast` with an existing `id` REPLACES that toast - meaning
 *  three quick failures of the same shape collapse into a single visible
 *  toast that just resets its timer. */
function dedupeId(err: unknown, message: string): string {
  const status = isApiError(err) ? err.status : 'x';
  // Truncate the message so minor formatting differences (e.g. trailing
  // punctuation, IDs in detail) don't bypass dedupe.
  return `err:${status}:${message.slice(0, 60)}`;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

interface NotifyOpts {
  /** Override the auto-generated toast id. Pass when you want to force a
   *  fresh toast (no dedupe) or to update an existing one. */
  id?: string;
  /** Duration in ms. Defaults to react-hot-toast's per-type defaults
   *  (success 3000, error 5000). */
  duration?: number;
}

export const notify = {
  success(message: string, opts?: NotifyOpts): string {
    return toast.success(message, opts);
  },

  /**
   * Show an error toast.
   *
   * `err` accepts anything - `ApiError`, generic `Error`, plain string,
   * or `unknown`. Use `fallback` to provide the verb-form copy the user
   * should see when the underlying error message is empty / generic.
   *
   * 401 errors are intentionally silent: the `SessionExpiredBanner`
   * surfaces them globally. Pass `opts.id` to force a toast anyway.
   */
  error(
    err: unknown,
    fallback?: string,
    opts?: NotifyOpts
  ): string | undefined {
    // 401 → banner handles it. Returning silently keeps mutation
    // `onError` callers honest without spamming a redundant toast.
    if (isApiError(err) && err.status === 401 && !opts?.id) {
      return undefined;
    }

    const message = resolveErrorMessage(err, fallback);
    const id = opts?.id ?? dedupeId(err, message);
    return toast.error(message, { id, duration: opts?.duration });
  },

  /**
   * An error toast with a short title and a sentence of explanation, for
   * failures where the caller knows exactly what was lost and the raw error
   * text would say less than the copy does (a rolled-back optimistic write,
   * say). Deduped on the title, like `error` is on its message.
   */
  errorWithDetail(
    { title, description }: { title: string; description: string },
    opts?: NotifyOpts
  ): string {
    const message = createElement(
      'div',
      null,
      createElement('p', { className: 'font-medium' }, title),
      createElement('p', { className: 'mt-0.5 text-xs' }, description)
    );
    return toast.error(message, {
      id: opts?.id ?? `err:detail:${title.slice(0, 60)}`,
      duration: opts?.duration,
    });
  },

  info(message: string, opts?: NotifyOpts): string {
    return toast(message, opts);
  },

  /**
   * A neutral toast with ONE follow-up button - "Take your break" when a
   * focus block ends. The Toaster has no action slot, so the message is a
   * node, like `errorWithDetail`; the button dismisses the toast before it
   * runs `onAction`, so the toast never outlives the thing it pointed at.
   */
  action(
    {
      title,
      description,
      actionLabel,
      onAction,
    }: {
      title: string;
      description?: string;
      actionLabel: string;
      onAction: () => void;
    },
    opts?: NotifyOpts
  ): string {
    return toast(
      t =>
        createElement(
          'div',
          { className: 'flex items-center gap-3' },
          createElement(
            'div',
            { className: 'min-w-0 flex-1' },
            createElement('p', { className: 'font-medium' }, title),
            description
              ? createElement(
                  'p',
                  {
                    className:
                      'mt-0.5 text-xs font-normal text-muted-foreground',
                  },
                  description
                )
              : null
          ),
          createElement(
            'button',
            {
              type: 'button',
              className:
                // 36px drawn, 44px on a phone - a toast's one action is
                // tapped in passing, at the bottom of the screen.
                'h-9 shrink-0 rounded-xl bg-primary px-3.5 text-sm font-semibold text-primary-foreground hover:bg-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring max-sm:h-11',
              onClick: () => {
                toast.dismiss(t.id);
                onAction();
              },
            },
            actionLabel
          )
        ),
      { id: opts?.id, duration: opts?.duration ?? 10_000 }
    );
  },

  /**
   * Wrap a promise with loading → success/error toasts. The `error`
   * callback receives the rejection value so it can do the same
   * `notify.error`-style message extraction as the rest of the API.
   */
  promise<T>(
    p: Promise<T>,
    messages: {
      loading: string;
      success: string | ((value: T) => string);
      error: string | ((err: unknown) => string);
    },
    opts?: NotifyOpts
  ): Promise<T> {
    return toast.promise(
      p,
      {
        loading: messages.loading,
        success: messages.success,
        error: err =>
          typeof messages.error === 'function'
            ? messages.error(err)
            : resolveErrorMessage(err, messages.error),
      },
      opts
    );
  },

  /** Dismiss a single toast by id, or all toasts when no id is given. */
  dismiss(id?: string): void {
    toast.dismiss(id);
  },
};

export type Notify = typeof notify;
